import { useEffect, useState } from "react";
import ProGate from "@/components/ProGate";
import { useAuth } from "@/context/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { ShieldCheck, Plus, Loader2, Trash2, MailPlus } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";

function TeamInner() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [email, setEmail] = useState("");
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/admin/admins").then(({ data }) => setData(data)).catch(() => toast.error("Could not load admins"));
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: r } = await api.post("/admin/admins", { email });
      toast.success(r.status === "admin" ? `${r.email} is now an admin` : `${r.email} will become admin when they sign up`);
      setEmail("");
      load();
    } catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
    finally { setSaving(false); }
  };
  const remove = async (em) => {
    try { await api.delete(`/admin/admins/${encodeURIComponent(em)}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err?.response?.data?.detail)); }
  };

  return (
    <div className="max-w-3xl mx-auto px-4 py-10" data-testid="admin-team-page">
      <div className="flex items-center gap-2 mb-1"><ShieldCheck className="h-5 w-5 text-emerald-400" /><h1 className="font-display text-2xl sm:text-3xl font-extrabold">Admins</h1></div>
      <p className="text-slate-400 text-sm mb-6">Admins see every catalyst, choose what Pro members see, manage codes and sources. Add an email — if they already have an account it's upgraded now, otherwise they become admin the moment they sign up (email or Google).</p>

      <Card className="bg-[#111723] border-[#222F43] p-5 mb-6">
        <form onSubmit={add} className="flex flex-col sm:flex-row gap-2 sm:items-end">
          <div className="flex-1"><label className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Email</label>
            <Input type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="name@example.com" className="bg-[#0B0F17] border-[#334566]" data-testid="admin-email-input" /></div>
          <Button type="submit" disabled={saving || !email} className="bg-emerald-500 hover:bg-emerald-400 text-black font-bold" data-testid="admin-add-btn">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Plus className="h-4 w-4 mr-1" /> Add admin</>}
          </Button>
        </form>
      </Card>

      {!data && <Loader2 className="h-5 w-5 animate-spin text-emerald-400" />}
      {data && (
        <div className="space-y-2">
          {data.admins.map((a) => (
            <Card key={a.email} className="bg-[#111723] border-[#222F43] p-4 flex items-center gap-3" data-testid={`admin-row-${a.email}`}>
              <ShieldCheck className="h-4 w-4 text-emerald-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm truncate">{a.email}</div>
                <div className="text-xs text-slate-500">@{a.username}{a.email === user?.email ? " · you" : ""}</div>
              </div>
              <Badge variant="outline" className="border-emerald-500/40 text-emerald-400 font-mono">admin</Badge>
              {a.email !== user?.email && (
                <button onClick={() => remove(a.email)} className="p-2 text-slate-500 hover:text-red-400" title="Remove admin" data-testid={`admin-remove-${a.email}`}><Trash2 className="h-4 w-4" /></button>
              )}
            </Card>
          ))}
          {data.invites.map((i) => (
            <Card key={i.email} className="bg-[#0e1119] border-dashed border-[#334566] p-4 flex items-center gap-3" data-testid={`admin-invite-${i.email}`}>
              <MailPlus className="h-4 w-4 text-amber-400 shrink-0" />
              <div className="flex-1 min-w-0">
                <div className="font-mono text-sm truncate">{i.email}</div>
                <div className="text-xs text-slate-500">becomes admin on first sign-in · added by {i.added_by}</div>
              </div>
              <Badge variant="outline" className="border-amber-500/40 text-amber-300 font-mono">invited</Badge>
              <button onClick={() => remove(i.email)} className="p-2 text-slate-500 hover:text-red-400" title="Cancel invite" data-testid={`admin-remove-${i.email}`}><Trash2 className="h-4 w-4" /></button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

export default function AdminTeam() {
  const { user } = useAuth();
  if (user && user.role !== "admin") return <div className="max-w-md mx-auto px-4 py-24 text-center text-slate-400">Admins only.</div>;
  return <ProGate feature="Admin"><TeamInner /></ProGate>;
}
